import { CSSProperties, FC, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import FilterPill from '../../components/shared/FilterPill/FilterPill';
import FilteringControl from '../../components/shared/FilteringControl/FilteringControl';
import SearchBarWithActions from '../../components/shared/SearchBarWithActions/SearchBarWithActions';
import {
  EvaluationTagDto,
  OverviewCounts,
  OverviewStatus,
} from '../../services/service-evaluations';
import BtChecklistSelect from '../bt-checklist-select/bt-checklist-select';
import { ChecklistItem } from '../bt-checklist-select/checklist';
import { EvaluationRequestStatus } from '../../util/evaluationContract';
import { UNEVALUATED_META, requestStatusMeta } from '../../util/evaluationStatusMeta';
import { EvaluationsView } from '../../util/evaluationsNavigation';
import {
  MAX_TAG_FILTERS,
  OVERVIEW_STATUSES,
  OverviewFilters,
  OverviewQuery,
  toggleStatus,
} from './overviewQuery';
import FilterChip from './FilterChip';
import styles from './app-evaluation-overview.module.scss';

type Props = {
  view: EvaluationsView;
  onViewChange: (view: EvaluationsView) => void;
  /** False in first use: there is nothing to filter yet. */
  showFilters: boolean;
  query: OverviewQuery;
  counts: OverviewCounts | null;
  tags: EvaluationTagDto[];
  onFilter: (change: Partial<OverviewFilters>) => void;
  search: string;
  onSearch: (value: string) => void;
  batchLabel: string | null;
};

const ARCHIVED_GROUP = '__archived__';

const STATUS_LABELS: Record<OverviewStatus, string> = {
  ongoing: 'Pågående',
  overdue: 'Försenade',
  finished: 'Avslutade',
  unevaluated: 'Ej utvärderade',
};

const STATUS_DOT_COLORS: Record<OverviewStatus, string> = {
  ongoing: requestStatusMeta(EvaluationRequestStatus.Active).color,
  overdue: requestStatusMeta(EvaluationRequestStatus.Overdue).color,
  finished: requestStatusMeta(EvaluationRequestStatus.Completed).color,
  unevaluated: UNEVALUATED_META.color,
};

const dotStyleOf = (status: OverviewStatus) =>
  ({ '--dot-color': STATUS_DOT_COLORS[status] } as CSSProperties);

const OverviewToolbar: FC<Props> = ({
  view,
  onViewChange,
  showFilters,
  query,
  counts,
  tags,
  onFilter,
  search,
  onSearch,
  batchLabel,
}) => {
  const [t] = useTranslation('evaluations');

  const tagOptions = useMemo(() => {
    const optionOf = (tag: EvaluationTagDto) => ({ value: tag._id, label: tag.name });
    const options: ChecklistItem[] = tags.filter(tag => !tag.archived).map(optionOf);
    const archived = tags.filter(tag => tag.archived).map(optionOf);
    if (!archived.length) return options;
    return [
      ...options,
      {
        value: ARCHIVED_GROUP,
        label: t('overview.tags.archived', 'Arkiverade'),
        options: archived,
      },
    ];
  }, [tags, t]);

  const viewSwitch = (
    <FilteringControl
      options={[
        { value: 'project', label: t('overview.view.project', 'Per projekt') },
        { value: 'supplier', label: t('overview.view.supplier', 'Per leverantör') },
      ]}
      value={view}
      onChange={value => onViewChange(value as EvaluationsView)}
    />
  );

  if (!showFilters) return <div className={styles.toolbarRow}>{viewSwitch}</div>;

  const statusLabel = (status: OverviewStatus) =>
    t(`overview.chips.${status}`, STATUS_LABELS[status]);
  const hasChips = query.statuses.length > 0 || query.tags.length > 0 || Boolean(query.batch);

  return (
    <>
      <SearchBarWithActions
        className={styles.toolbar}
        value={search}
        onChange={onSearch}
        placeholder={t('overview.search', 'Sök på titel, leverantör eller projekt')}
      >
        <div className={styles.tagFilter}>
          <BtChecklistSelect
            placeholder={t('overview.tags.placeholder', 'Filtrera på tagg')}
            options={tagOptions}
            value={query.tags}
            onChange={tagIds => {
              if (tagIds.length <= MAX_TAG_FILTERS) onFilter({ tags: tagIds });
            }}
          />
          {query.tags.length >= MAX_TAG_FILTERS && (
            <p className={`${styles.tagLimit} ${styles.small} ${styles.muted}`}>
              {t('overview.tags.max', 'Högst {{max}} taggar i filtret', { max: MAX_TAG_FILTERS })}
            </p>
          )}
        </div>
        {viewSwitch}
      </SearchBarWithActions>
      <div className={styles.statusFilters}>
        <FilterPill
          label={t('overview.chips.all', 'Alla')}
          active={!query.statuses.length}
          count={counts?.all}
          onClick={() => onFilter({ statuses: [] })}
        />
        {OVERVIEW_STATUSES.map(status => (
          <FilterPill
            key={status}
            label={statusLabel(status)}
            active={query.statuses.includes(status)}
            dotStyle={dotStyleOf(status)}
            count={counts?.[status]}
            onClick={() => onFilter({ statuses: toggleStatus(query.statuses, status) })}
          />
        ))}
      </div>
      {hasChips && (
        <div className={styles.filterChips}>
          {query.batch && batchLabel && (
            <FilterChip
              clearName={batchLabel}
              label={batchLabel}
              onClear={() => onFilter({ batch: null })}
            />
          )}
          <button
            type="button"
            className={styles.quietAction}
            onClick={() => onFilter({ statuses: [], tags: [], batch: null })}
          >
            {t('overview.clearAll', 'Rensa alla')}
          </button>
        </div>
      )}
    </>
  );
};

export default OverviewToolbar;
